import React, { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { ArrowRight, TrendingUp, Clock, ShieldCheck, Award } from "lucide-react";

const SECTOR_ICONS = [TrendingUp, ShieldCheck, Award, Clock];

const CaseStudies = ({ onRequestDetails }) => {
  const { t } = useI18n();
  const [active, setActive] = useState(0);
  const items = t.caseStudies.items;
  const labels = t.caseStudies.labels;
  const current = items[active];
  const Icon = SECTOR_ICONS[active] || TrendingUp;

  return (
    <section id="case-studies" className="py-20 md:py-32 bg-white" data-testid="case-studies-section">
      <div className="zdc-container">
        <div className="max-w-2xl mb-12 md:mb-16">
          <div className="text-xs font-medium uppercase tracking-[0.18em] text-[#2563EB] mb-3">
            {t.caseStudies.eyebrow}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#111111]">
            {t.caseStudies.title}
          </h2>
          <p className="mt-4 text-base md:text-lg text-[#6B7280]">{t.caseStudies.subtitle}</p>
        </div>

        {/* Tab selector */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 mb-6 md:mb-8 -mx-6 px-6 md:mx-0 md:px-0">
          {items.map((it, i) => (
            <button
              key={i}
              data-testid="case-study-tab"
              data-active={active === i}
              onClick={() => setActive(i)}
              className={`flex-shrink-0 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${
                active === i
                  ? "bg-[#111111] text-white border-[#111111] shadow-[0_8px_20px_rgba(17,17,17,0.18)]"
                  : "bg-white text-[#111111] border-[#E5E7EB] hover:border-[#111111]/30 hover:bg-[#F5F7FA]"
              }`}
            >
              {it.sector}
            </button>
          ))}
        </div>

        {/* Active case study card */}
        <div
          key={active}
          data-testid="case-study-card"
          className="rounded-2xl border border-[#E5E7EB] bg-[#F5F7FA] overflow-hidden reveal"
        >
          <div className="grid lg:grid-cols-12 gap-px bg-[#E5E7EB]">
            {/* Left: context */}
            <div className="lg:col-span-5 bg-white p-7 md:p-10">
              <div className="flex items-start gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-[#111111] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-white" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="font-display text-lg font-semibold text-[#111111] leading-tight">
                    {current.sector}
                  </div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{current.size}</div>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#2563EB] font-medium mb-1.5">
                    {labels.challenge}
                  </div>
                  <p className="text-sm text-[#111111] leading-relaxed">{current.challenge}</p>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#2563EB] font-medium mb-1.5">
                    {labels.solution}
                  </div>
                  <p className="text-sm text-[#111111] leading-relaxed">{current.solution}</p>
                </div>

                <div className="pt-3 border-t border-[#E5E7EB] flex items-center justify-between">
                  <span className="text-xs text-[#6B7280]">{labels.duration}</span>
                  <span className="text-sm font-medium text-[#111111]">{current.duration}</span>
                </div>
              </div>
            </div>

            {/* Right: metrics */}
            <div className="lg:col-span-7 bg-white p-7 md:p-10">
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium mb-4">
                KPI · Pre / Post
              </div>

              <div className="space-y-3">
                {current.metrics.map((m, i) => (
                  <div
                    key={i}
                    data-testid="case-study-metric"
                    className="flex flex-wrap items-center gap-3 p-4 rounded-xl border border-[#E5E7EB] bg-[#F5F7FA]"
                  >
                    <div className="flex-1 min-w-[140px] text-sm font-medium text-[#111111]">
                      {m.label}
                    </div>
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-[#6B7280]">{labels.before}</div>
                        <div className="font-display text-base font-semibold text-[#6B7280] line-through decoration-[#D1D5DB]">
                          {m.before}
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#2563EB]" />
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-[#2563EB] font-medium">{labels.after}</div>
                        <div className="font-display text-base font-semibold text-[#111111]">{m.after}</div>
                      </div>
                    </div>
                    <span className="ml-auto inline-block px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#10B981] text-[11px] font-semibold">
                      {m.delta}
                    </span>
                  </div>
                ))}
              </div>

              {/* Aggregate delta strip */}
              <div className="mt-5 p-4 rounded-xl bg-[#111111] text-white flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#2563EB] flex items-center justify-center">
                    <TrendingUp className="w-4 h-4" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-white/60">Delta</div>
                    <div className="text-sm font-medium">
                      {current.metrics.map((m) => m.delta).join(" · ")}
                    </div>
                  </div>
                </div>
                <button
                  onClick={onRequestDetails}
                  data-testid="case-study-request-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-[#111111] text-xs font-medium hover:bg-[#F5F7FA] transition-colors"
                >
                  {t.caseStudies.cta}
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
